// src/components/admin/settings/HeroSliderSettings.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp, FaImage } from 'react-icons/fa';
import './SettingsPages.css';

// TODO: business rule, not from any spec doc — the max dimensions/size
// requested by the client. Easy to change if they revise these numbers.
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const SLIDE_COUNT = 2; // reduced from 3 per client requirement

const HeroSliderSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlider, setUploadingSlider] = useState({});
  const [expanded, setExpanded] = useState({ 1: true, 2: false });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSlide = (slideNum) => {
    setExpanded(prev => ({ ...prev, [slideNum]: !prev[slideNum] }));
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const validateImageClientSide = (file) => {
    return new Promise((resolve) => {
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`Image must be under ${MAX_SIZE_MB}MB (this file is ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        resolve(false);
        return;
      }

      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
          toast.error(`Image must be ${MAX_WIDTH}×${MAX_HEIGHT}px or smaller (this image is ${img.width}×${img.height}px)`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        toast.error('Could not read image dimensions — file may be corrupted');
        resolve(false);
      };
      img.src = objectUrl;
    });
  };

  const handleSliderImageUpload = async (e, slideNum) => {
    const file = e.target.files[0];
    if (!file) return;

    const isValid = await validateImageClientSide(file);
    if (!isValid) {
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('slider_image', file);
    setUploadingSlider(prev => ({ ...prev, [slideNum]: true }));

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-slider-image.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setSettings({ ...settings, [`slider_image_${slideNum}`]: data.image_url });
        toast.success(`Slide ${slideNum} image uploaded!`);
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingSlider(prev => ({ ...prev, [slideNum]: false }));
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Only the button LINK is forced to /shop for every slide — title,
    // subtitle, and button text stay exactly as the admin typed them.
    const payload = { ...settings };
    for (let i = 1; i <= SLIDE_COUNT; i++) {
      payload[`slider_button_link_${i}`] = '/shop';
    }

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setSettings(payload);
        toast.success('Hero slider settings saved!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loader">Loading hero slider...</div>;

  return (
    <div className="settings-page">
      <h2>Hero Slider Settings</h2>
      <p className="section-description">Manage the {SLIDE_COUNT} slides shown on the homepage</p>

      <form onSubmit={handleSubmit} className="settings-form">
        {Array.from({ length: SLIDE_COUNT }, (_, i) => i + 1).map((slideNum) => {
          const isOpen = expanded[slideNum];
          const imageUrl = settings[`slider_image_${slideNum}`];

          return (
            <div key={slideNum} className="accordion-slide-card">
              <button
                type="button"
                className="accordion-slide-header"
                onClick={() => toggleSlide(slideNum)}
              >
                <div className="accordion-slide-header-left">
                  {imageUrl ? (
                    <img src={`${imageUrl}?t=${Date.now()}`} alt={`Slide ${slideNum} thumbnail`} className="accordion-thumb" />
                  ) : (
                    <div className="accordion-thumb accordion-thumb-empty"><FaImage /></div>
                  )}
                  <span>Slide {slideNum}</span>
                </div>
                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {isOpen && (
                <div className="accordion-slide-body">
                  <div className="form-group">
                    <label>Slide Image</label>

                    {imageUrl && (
                      <div className="image-preview">
                        <img src={`${imageUrl}?t=${Date.now()}`} alt={`Slide ${slideNum}`} />
                        <button
                          type="button"
                          className="remove-img-btn"
                          onClick={() => setSettings({ ...settings, [`slider_image_${slideNum}`]: '' })}
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSliderImageUpload(e, slideNum)}
                      disabled={uploadingSlider[slideNum]}
                    />
                    <small className="image-size-hint">
                      Recommended: {MAX_WIDTH}×{MAX_HEIGHT}px, max {MAX_SIZE_MB}MB.
                    </small>
                    {uploadingSlider[slideNum] && <small className="uploading-hint">Uploading...</small>}
                  </div>

                  <div className="form-group">
                    <label>Slide Title</label>
                    <input
                      type="text"
                      name={`slider_title_${slideNum}`}
                      value={settings[`slider_title_${slideNum}`] || ''}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Slide Subtitle</label>
                    <textarea
                      name={`slider_subtitle_${slideNum}`}
                      value={settings[`slider_subtitle_${slideNum}`] || ''}
                      onChange={handleChange}
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label>Button Text</label>
                    <input
                      type="text"
                      name={`slider_button_text_${slideNum}`}
                      value={settings[`slider_button_text_${slideNum}`] || 'Shop Now'}
                      onChange={handleChange}
                    />
                    <small className="image-size-hint">
                      This button always links to the Shop page.
                    </small>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button type="submit" disabled={saving} className="save-btn">
          {saving ? 'Saving...' : 'Save Hero Slider'}
        </button>
      </form>
    </div>
  );
};

export default HeroSliderSettings;